import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import { getAnonymousId } from 'src/utils/getAnonymousId'
import { createRandomId } from 'src/utils/createRandomId'
import { isInternalTester } from 'src/utils/internalTester'
import {
  FLUSH_INTERVAL_MS,
  MAX_BATCH_SIZE,
  MAX_QUEUE_SIZE,
  TRACKING_ENDPOINT_PATH,
} from './constants'
import {
  TrackingEvent,
  TrackingEventInput,
  TrackingPayload,
} from './interfaces/trackingApi.interface'

const ENDPOINT = `${ANALYTICS_API_URL}${TRACKING_ENDPOINT_PATH}`

let queue: TrackingEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | undefined

const isTrackingEnabled = (): boolean =>
  process.env.REACT_APP_TRACKING_DISABLED !== '1'

const sendWithBeacon = (body: string): boolean => {
  try {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.sendBeacon !== 'function'
    ) {
      return false
    }

    return navigator.sendBeacon(
      ENDPOINT,
      new Blob([body], { type: 'text/plain' })
    )
  } catch (error) {
    return false
  }
}

const sendWithFetch = (body: string, sessionId?: string): void => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    if (sessionId) {
      headers['x-session-id'] = sessionId
    }

    // keepalive: a flush that starts as the user navigates away still completes.
    // The catch is the whole error policy: a failed send is a dropped event, nothing more.
    fetch(ENDPOINT, { body, headers, keepalive: true, method: 'POST' }).catch(
      () => undefined
    )
  } catch (error) {
    // Never surface a transport problem to the app.
  }
}

const cancelScheduledFlush = (): void => {
  if (flushTimer !== undefined) {
    clearTimeout(flushTimer)
    flushTimer = undefined
  }
}

/**
 * Send everything buffered.
 *
 * @param useBeacon - true only while the document is going away (pagehide). Everywhere else the
 * fetch path is better: it carries the session header and reports nothing back either way.
 */
export const flushTracking = (useBeacon = false): void => {
  try {
    cancelScheduledFlush()

    if (queue.length === 0) {
      return
    }

    const sessionId = getSessionIdFromLocalStorage()
    const internal = isInternalTester()
    const pending = queue
    queue = []

    for (let index = 0; index < pending.length; index += MAX_BATCH_SIZE) {
      const payload: TrackingPayload = {
        events: pending.slice(index, index + MAX_BATCH_SIZE),
      }

      if (internal) {
        payload.internal = true
      }

      if (sessionId) {
        payload.sessionId = sessionId
      }

      const body = JSON.stringify(payload)

      if (!useBeacon || !sendWithBeacon(body)) {
        sendWithFetch(body, sessionId)
      }
    }
  } catch (error) {
    // A tracker that throws during a page transition would be worse than one that loses a batch.
  }
}

const scheduleFlush = (): void => {
  if (flushTimer !== undefined) {
    return
  }

  flushTimer = setTimeout(() => {
    flushTimer = undefined
    flushTracking()
  }, FLUSH_INTERVAL_MS)
}

export const trackEvent = (event: TrackingEventInput): void => {
  try {
    if (!isTrackingEnabled()) {
      return
    }

    const anonymousId = getAnonymousId()
    const entry: TrackingEvent = {
      ...event,
      eventId: createRandomId(),
      ts: new Date().toISOString(),
    }

    if (anonymousId) {
      entry.anonymousId = anonymousId
    }

    queue.push(entry)

    if (queue.length > MAX_QUEUE_SIZE) {
      queue = queue.slice(-MAX_QUEUE_SIZE)
    }

    if (queue.length >= MAX_BATCH_SIZE) {
      flushTracking()

      return
    }

    scheduleFlush()
  } catch (error) {}
}

export const trackLogin = (): void => {
  trackEvent({ eventType: 'login' })
}

export const trackLogout = (): void => {
  trackEvent({ eventType: 'logout' })
  flushTracking()
}
